"use client";

import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useWorkspace } from "@/lib/context/WorkspaceContext";
import { getViewerTier } from "@/lib/permissions";
import { fetchWithAuth, API_BASE } from "@/lib/auth";
import { Shield, ShieldAlert, Users, ClipboardList, Briefcase, Database, AlertCircle, RefreshCw, Plus, X as CloseIcon, ArrowLeft, Info } from "lucide-react";

import ConsolePostingTable from "./ConsolePostingTable";
import ConsoleInterestQueue from "./ConsoleInterestQueue";
import ConsoleAssignmentTable from "./ConsoleAssignmentTable";
import ConsoleVolunteerView from "./ConsoleVolunteerView";
import GovernanceContributionTable from "./GovernanceContributionTable";
import ConsoleApprovalQueue from "./ConsoleApprovalQueue";
import ConsoleGigTypesPanel from "./ConsoleGigTypesPanel";
import ConsoleStandingPoolsTable from "./ConsoleStandingPoolsTable";
import { ConsoleIntroNote } from "./ConsoleIntroNote";


interface GigsConsolePanelProps {
    isEmbedded?: boolean;
    hideHeader?: boolean;
    isLeader?: boolean;
    returnUrl?: string;
}

export default function GigsConsolePanel({ isEmbedded = false, hideHeader = false, isLeader = false, returnUrl }: GigsConsolePanelProps) {
    const { user, isLoading: isUserLoading } = useWorkspace();
    const router = useRouter();
    const [activeLens, setActiveLens] = useState<string>("");
    const [activeSubTab, setActiveSubTab] = useState<string>("postings");
    
    // Data states
    const [postings, setPostings] = useState([]);
    const [applications, setApplications] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [pendingPostings, setPendingPostings] = useState([]);
    const [ethicsIntakes, setEthicsIntakes] = useState([]);
    
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [dataError, setDataError] = useState<string | null>(null);
    const [selectedVolunteerId, setSelectedVolunteerId] = useState<string | null>(null);

    // 1. Auth and lens permission derivation
    const isStaffOrAdmin = user ? (user.is_staff || ["admin", "superadmin", "staff"].includes((user.role || "").toLowerCase())) : false;
    const isBoardLiaison = user ? (user.assignments?.some(a => (a.role || "").toLowerCase().includes("board")) || false) : false;
    const isEthicsAuthorized = user ? (
        (user.role || "").toLowerCase() === "ethics_officer" ||
        user.assignments?.some(a => (a.role || "").toLowerCase().includes("ethics"))
    ) : false;
    const viewerTier = getViewerTier(user);

    const availableLenses: { id: string; label: string; desc: string; }[] = [];
    if (user) {
        if (isStaffOrAdmin) {
            availableLenses.push({ id: "staff_global", label: "Global Operations", desc: "Omnipotent view of all platform gig operations." });
            availableLenses.push({ id: "governance", label: "Governance Lens", desc: "Strategic oversight, metrics, and compliance logs." });
        } else {
            // Committee and Chapter Chairs can see postings owned by their assigned entities
            availableLenses.push({ id: "owning_group", label: "Owning Group", desc: "Gigs created and managed directly by your group." });
            availableLenses.push({ id: "supporting_regional", label: "Regional Support", desc: "External postings where your group provides regional support." });
            
            if (isBoardLiaison) {
                availableLenses.push({ id: "governance", label: "Governance Lens", desc: "Strategic oversight, metrics, and compliance logs." });
            }
        }
        
        if (isEthicsAuthorized) {
            availableLenses.push({ id: "ethics", label: "Ethics Lens", desc: "Confidential intake, incident management, and policy compliance." });
        }
    }

    // 2. Initialize default lens
    useEffect(() => {
        if (availableLenses.length > 0 && !activeLens) {
            setActiveLens(availableLenses[0].id);
        }
    }, [availableLenses, activeLens]);

    // Adjust sub-tabs when switching to Governance or Ethics lens
    useEffect(() => {
        if (activeLens === "governance") {
            setActiveSubTab("governance_overview");
        } else if (activeLens === "ethics") {
            setActiveSubTab("ethics_intake");
        } else if (activeSubTab === "governance_overview" || activeSubTab === "ethics_intake" || activeSubTab === "gig_types") {
            setActiveSubTab("postings");
        }
    }, [activeLens]);

    // 3. Data fetching engine
    const fetchLensData = async () => {
        if (!activeLens) return;
        
        // Governance Overview and Gig Management tabs manage their own fetching via subcomponents
        if (activeLens === "governance") {
            return;
        }

        setIsLoadingData(true);
        setDataError(null);

        try {
            // Fetch postings, queue, and assignments in parallel to populate all badge counts immediately
            const promises = [
                fetchWithAuth(`${API_BASE}/api/v1/gigs/console/postings?lens=${activeLens}`),
                fetchWithAuth(`${API_BASE}/api/v1/gigs/console/queue?lens=${activeLens}`),
                fetchWithAuth(`${API_BASE}/api/v1/gigs/console/assignments?lens=${activeLens}`)
            ];

            if (activeLens === "staff_global") {
                promises.push(fetchWithAuth(`${API_BASE}/api/v1/gigs/console/approval-queue`));
            }
            if (activeLens === "ethics") {
                promises.push(fetchWithAuth(`${API_BASE}/api/v1/gigs/console/ethics/intake`));
            }

            const responses = await Promise.all(promises);
            
            for (const res of responses) {
                if (!res.ok) {
                    const errBody = await res.json().catch(() => ({}));
                    throw new Error(errBody.detail || "Failed to query operations data.");
                }
            }

            const postingsResult = await responses[0].json();
            const queueResult = await responses[1].json();
            const assignmentsResult = await responses[2].json();

            const livePostings = postingsResult.filter((p: any) => 
                p.status.toLowerCase() !== "pending_staff_review" && p.status.toLowerCase() !== "completed"
            );
            setPostings(livePostings);

            const pendingApps = queueResult.filter((a: any) => 
                a.status.toLowerCase() === "submitted" || a.status.toLowerCase() === "in_candidate_pool"
            );
            setApplications(pendingApps);

            const activeAssigns = assignmentsResult.filter((a: any) => 
                a.status.toLowerCase() !== "completed" && a.status.toLowerCase() !== "cancelled" && a.status.toLowerCase() !== "removed"
            );
            setAssignments(activeAssigns);

            let resIdx = 3;
            if (activeLens === "staff_global") {
                const approvalResult = await responses[resIdx++].json();
                setPendingPostings(approvalResult);
            }
            if (activeLens === "ethics") {
                const ethicsResult = await responses[resIdx++].json();
                setEthicsIntakes(ethicsResult);
            }
        } catch (err: any) {
            console.error("Error fetching console data:", err);
            setDataError(err.message || "An unexpected error occurred while fetching platform records.");
        } finally {
            setIsLoadingData(false);
        }
    };

    useEffect(() => {
        Promise.resolve().then(() => {
            fetchLensData();
        });
    }, [activeLens]);

    if (isUserLoading) {
        return (
            <div className="min-h-[40vh] flex flex-col items-center justify-center gap-4">
                <div className="w-10 h-10 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#003d45]">Initialising Operations Console...</p>
            </div>
        );
    }

    // Access Denied if volunteer/general member tries to access
    const hasAssignments = user?.assignments && user.assignments.length > 0;
    if (!user || (viewerTier === "volunteer" && !hasAssignments)) {
        return (
            <div className="max-w-md mx-auto my-16 text-center bg-white border border-gray-150 shadow-xl rounded-xl p-8 animate-in fade-in duration-200">
                <ShieldAlert className="w-12 h-12 text-[#007a8a] mx-auto mb-4" />
                <h2 className="text-lg font-black text-[#003d45] uppercase tracking-wider">Access Restricted</h2>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                    The Volunteer Gigs Operations Console is restricted to CMT Association Staff, Administrators, and active Committee/Chapter Chairs.
                </p>
                <div className="mt-6 pt-6 border-t border-gray-150 flex justify-center">
                    <a 
                        href="/dashboard"
                        className="bg-[#007a8a] text-white px-5 py-2.5 rounded-xl text-xs font-semibold hover:bg-[#00606d] transition-all no-underline"
                    >
                        Return to Dashboard
                    </a>
                </div>
            </div>
        );
    }

    const currentLensObj = availableLenses.find(l => l.id === activeLens);
    return (
        <div className={isEmbedded ? "space-y-6 animate-in fade-in duration-200 font-sans" : "space-y-8 max-w-7xl mx-auto px-4 sm:px-6 py-6 animate-in fade-in duration-200 font-sans"}>

            {/* Embedded group-style full header (mirrors staff header layout) */}
            {isEmbedded && hideHeader && (
                <div className="space-y-5">
                    {/* Title row */}
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 border-b border-gray-150 pb-5">
                        <div>
                            <span className="font-sans font-bold text-[11px] uppercase tracking-[0.16em] text-[#4b8892] block">
                                Group Gigs Console
                            </span>
                            <h2 className="font-sans font-black uppercase mt-1 leading-none" style={{ fontSize: "28px", letterSpacing: "0.02em" }}>
                                <span className="text-[#003d45]">YOUR </span>
                                <span className="text-[#4b8892]">GIGS</span>
                            </h2>
                            <p className="text-xs text-gray-400 mt-2 font-medium">
                                {currentLensObj?.desc}
                            </p>
                        </div>

                        {(isLeader || isStaffOrAdmin || (user?.assignments && user.assignments.length > 0)) && (
                            <div className="flex flex-col items-end gap-1 shrink-0 select-none">
                                <button
                                    onClick={() => router.push(
                                        returnUrl
                                            ? `/dashboard/gigs/create?returnTo=${encodeURIComponent(returnUrl)}`
                                            : "/dashboard/gigs/create"
                                    )}
                                    className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#007a8a] hover:bg-[#006977] text-white rounded-lg text-xs font-bold uppercase tracking-wider transition-colors shadow-sm border-none cursor-pointer"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    Create Gig
                                </button>
                                <span className="text-[10px] text-gray-400 font-semibold tracking-wide block">
                                    New gigs go to a staff review queue before they&apos;re live.
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Info banner */}
                    <div className="flex items-start gap-3 bg-[#c3d6d9]/30 rounded-lg px-4 py-3.5">
                        <span className="w-1 shrink-0 self-stretch rounded-full bg-[#4b8892]" />
                        <p className="text-sm text-[#003d45] leading-relaxed">
                            You manage gigs for your group. Create a posting and our team reviews it before it goes live to eligible volunteers — the eligibility gate does the filtering, so you never have to. You can track applicants and manage assignments from this console.
                        </p>
                    </div>
                </div>
            )}

            {/* Page Header */}
            {!hideHeader && (
                <div className="space-y-5">
                    {/* Title row */}
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 border-b border-gray-150 pb-5">
                        <div className="flex items-center gap-4">
                            {!isEmbedded && (
                                <button 
                                    onClick={() => router.push("/dashboard?section=gigs")}
                                    className="w-10 h-10 rounded-full bg-white border border-gray-250 flex items-center justify-center hover:bg-gray-50 transition-colors shrink-0 shadow-sm cursor-pointer"
                                    title="Back to My Gigs"
                                >
                                    <ArrowLeft className="w-4 h-4 text-gray-600" />
                                </button>
                            )}
                            <div>
                                <span className="font-sans font-bold text-[11px] uppercase tracking-[0.16em] text-[#4b8892] block">
                                    Cross-cutting Gigs console
                                </span>
                                <h1 className="font-sans font-black uppercase mt-1 leading-none" style={{ fontSize: "28px", letterSpacing: "0.02em" }}>
                                    <span className="text-[#003d45]">THE WHOLE </span>
                                    <span className="text-[#4b8892]">PICTURE</span>
                                </h1>
                                <p className="text-xs text-gray-400 mt-2 font-medium">
                                    {currentLensObj?.desc}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col items-end gap-1 shrink-0 select-none">
                            <button
                                onClick={() => router.push("/dashboard/gigs/create")}
                                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#007a8a] hover:bg-[#006977] text-white rounded-lg text-xs font-bold uppercase tracking-wider transition-colors shadow-sm border-none cursor-pointer"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Create Gig
                            </button>
                            <span className="text-[10px] text-gray-400 font-semibold tracking-wide block">
                                New gigs go to a staff review queue before they're live.
                            </span>
                        </div>
                    </div>

                    {/* Info banner — matches the mist strip in the UI reference */}
                    <div className="flex items-start gap-3 bg-[#c3d6d9]/30 rounded-lg px-4 py-3.5">
                        <span className="w-1 shrink-0 self-stretch rounded-full bg-[#4b8892]" />
                        <p className="text-sm text-[#003d45] leading-relaxed">
                            You review gigs for all groups.  Approving one publishes it to every eligible volunteer — the eligibility gate does the filtering, so you never have to. You can edit before approving, or send back with a note.
                        </p>
                    </div>
                </div>
            )}

            {/* First-visit explainer — "gig" is jargon nothing else on this
                surface defines. Mounted here so every header variant gets it. */}
            <div className="flex justify-end -mb-2">
                <ConsoleIntroNote isStaffView={!hideHeader} />
            </div>

            {/* Lens Selector (Security Scopes) Card */}
            <div className="bg-white border border-gray-150 rounded-xl p-4 shadow-sm flex flex-col gap-2.5">
                <span className="font-sans font-bold text-[10.5px] uppercase tracking-[0.16em] text-[#007a8a]">
                    Access Scope Lens
                </span>
                <div className="flex flex-wrap gap-2">
                    {availableLenses.map((lens) => (
                        <button
                            key={lens.id}
                            onClick={() => setActiveLens(lens.id)}
                            className={`px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
                                activeLens === lens.id 
                                    ? "bg-[#004d57] text-white shadow-sm border-none" 
                                    : "bg-white border border-gray-200 text-[#003d45] hover:bg-gray-50/80"
                            }`}
                        >
                            {lens.label}
                        </button>
                    ))}
                </div>
                {/* Description helper for the selected lens */}
                {currentLensObj && (
                    <div className="mt-2 bg-[#f0f9fa]/60 border border-[#007a8a]/10 rounded-lg p-3 flex gap-2 items-start animate-in fade-in slide-in-from-top-1 duration-150">
                        <Info className="w-4 h-4 text-[#007a8a] shrink-0 mt-0.5" />
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-wider text-[#007a8a] block">Selected Scope Detail</span>
                            <p className="text-[11.5px] font-medium text-slate-650 mt-0.5 leading-relaxed">{currentLensObj.desc}</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Sub-tab Navigation */}
            <div className="flex flex-col gap-6">
                <div className="flex flex-wrap gap-2.5 pb-2 border-b border-gray-150 overflow-x-auto shrink-0 bg-transparent select-none">
                    {activeLens === "governance" && (
                        <>
                            <button
                                onClick={() => setActiveSubTab("governance_overview")}
                                className={`group flex items-center px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-150 cursor-pointer ${
                                    activeSubTab === "governance_overview" 
                                        ? "bg-[#004d57] text-white border-2 border-[#004d57] shadow-sm" 
                                        : "bg-transparent text-[#4b8892] border-2 border-transparent hover:text-[#004d57] hover:bg-[#004d57]/5"
                                }`}
                            >
                                <span className={`w-1 h-3.5 rounded-full mr-2 transition-colors ${
                                    activeSubTab === "governance_overview" ? "bg-[#8bc4c9]" : "bg-gray-300/60 group-hover:bg-[#8bc4c9]/60"
                                }`} />
                                Analytics & Audits
                            </button>
                            <button
                                onClick={() => setActiveSubTab("gig_types")}
                                className={`group flex items-center px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-150 cursor-pointer ${
                                    activeSubTab === "gig_types" 
                                        ? "bg-[#004d57] text-white border-2 border-[#004d57] shadow-sm" 
                                        : "bg-transparent text-[#4b8892] border-2 border-transparent hover:text-[#004d57] hover:bg-[#004d57]/5"
                                }`}
                            >
                                <span className={`w-1 h-3.5 rounded-full mr-2 transition-colors ${
                                    activeSubTab === "gig_types" ? "bg-[#8bc4c9]" : "bg-gray-300/60 group-hover:bg-[#8bc4c9]/60"
                                }`} />
                                Gig Management
                            </button>
                        </>
                    )}
                    {activeLens === "ethics" && (
                        <button
                            onClick={() => setActiveSubTab("ethics_intake")}
                            className={`group flex items-center px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-150 cursor-pointer ${
                                activeSubTab === "ethics_intake" 
                                    ? "bg-[#004d57] text-white border-2 border-[#004d57] shadow-sm" 
                                    : "bg-transparent text-[#4b8892] border-2 border-transparent hover:text-[#004d57] hover:bg-[#004d57]/5"
                                }`}
                        >
                            <span className={`w-1 h-3.5 rounded-full mr-2 transition-colors ${
                                activeSubTab === "ethics_intake" ? "bg-[#8bc4c9]" : "bg-gray-300/60 group-hover:bg-[#8bc4c9]/60"
                            }`} />
                            Ethics Intake Queue
                            {ethicsIntakes.length > 0 && (
                                <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-black bg-[#f9d2d3] text-[#004d57]">
                                    {ethicsIntakes.length}
                                </span>
                            )}
                        </button>
                    )}
                    {activeLens === "staff_global" && (
                        <button
                            onClick={() => setActiveSubTab("review_queue")}
                            className={`group flex items-center px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-150 cursor-pointer ${
                                activeSubTab === "review_queue" 
                                    ? "bg-[#004d57] text-white border-2 border-[#004d57] shadow-sm" 
                                    : "bg-transparent text-[#4b8892] border-2 border-transparent hover:text-[#004d57] hover:bg-[#004d57]/5"
                                }`}
                        >
                            <span className={`w-1 h-3.5 rounded-full mr-2 transition-colors ${
                                activeSubTab === "review_queue" ? "bg-[#8bc4c9]" : "bg-gray-300/60 group-hover:bg-[#8bc4c9]/60"
                            }`} />
                            Review Queue
                            {pendingPostings.length > 0 && (
                                <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-black bg-[#f9d2d3] text-[#004d57]">
                                    {pendingPostings.length}
                                </span>
                            )}
                        </button>
                    )}
                    {activeLens !== "governance" && (
                        <>
                            <button
                                onClick={() => setActiveSubTab("postings")}
                                className={`group flex items-center px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-150 cursor-pointer ${
                                    activeSubTab === "postings" 
                                        ? "bg-[#004d57] text-white border-2 border-[#004d57] shadow-sm" 
                                        : "bg-transparent text-[#4b8892] border-2 border-transparent hover:text-[#004d57] hover:bg-[#004d57]/5"
                                }`}
                            >
                                <span className={`w-1 h-3.5 rounded-full mr-2 transition-colors ${
                                    activeSubTab === "postings" ? "bg-[#8bc4c9]" : "bg-gray-300/60 group-hover:bg-[#8bc4c9]/60"
                                }`} />
                                Postings
                                {postings.length > 0 && (
                                    <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-black bg-[#f9d2d3] text-[#004d57]">
                                        {postings.length}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => setActiveSubTab("queue")}
                                className={`group flex items-center px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-150 cursor-pointer ${
                                    activeSubTab === "queue" 
                                        ? "bg-[#004d57] text-white border-2 border-[#004d57] shadow-sm" 
                                        : "bg-transparent text-[#4b8892] border-2 border-transparent hover:text-[#004d57] hover:bg-[#004d57]/5"
                                }`}
                            >
                                <span className={`w-1 h-3.5 rounded-full mr-2 transition-colors ${
                                    activeSubTab === "queue" ? "bg-[#8bc4c9]" : "bg-gray-300/60 group-hover:bg-[#8bc4c9]/60"
                                }`} />
                                Interest Queue
                                {applications.length > 0 && (
                                    <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-black bg-[#f9d2d3] text-[#004d57]">
                                        {applications.length}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => setActiveSubTab("assignments")}
                                className={`group flex items-center px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-150 cursor-pointer ${
                                    activeSubTab === "assignments" 
                                        ? "bg-[#004d57] text-white border-2 border-[#004d57] shadow-sm" 
                                        : "bg-transparent text-[#4b8892] border-2 border-transparent hover:text-[#004d57] hover:bg-[#004d57]/5"
                                }`}
                            >
                                <span className={`w-1 h-3.5 rounded-full mr-2 transition-colors ${
                                    activeSubTab === "assignments" ? "bg-[#8bc4c9]" : "bg-gray-300/60 group-hover:bg-[#8bc4c9]/60"
                                }`} />
                                Active Assignments
                                {assignments.length > 0 && (
                                    <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-black bg-[#f9d2d3] text-[#004d57]">
                                        {assignments.length}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => setActiveSubTab("pools")}
                                className={`group flex items-center px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-150 cursor-pointer ${
                                    activeSubTab === "pools" 
                                        ? "bg-[#004d57] text-white border-2 border-[#004d57] shadow-sm" 
                                        : "bg-transparent text-[#4b8892] border-2 border-transparent hover:text-[#004d57] hover:bg-[#004d57]/5"
                                }`}
                            >
                                <span className={`w-1 h-3.5 rounded-full mr-2 transition-colors ${
                                    activeSubTab === "pools" ? "bg-[#8bc4c9]" : "bg-gray-300/60 group-hover:bg-[#8bc4c9]/60"
                                }`} />
                                Standing Pools
                            </button>
                        </>
                    )}
                </div>

                {/* Main Content Renderers */}
                {/* Header Information for the Active Tab */}
                {activeSubTab === "review_queue" && (
                    <div className="mb-6 animate-in fade-in duration-200">
                        <div className="text-[10px] font-black uppercase tracking-widest text-[#007a8a]">Your endorsement</div>
                        <h3 className="text-xl font-bold text-[#003d45] mt-1 mb-2">Review Queue</h3>
                        <p className="text-xs text-gray-500 max-w-2xl leading-relaxed">
                            Gigs your members drafted. Approve/edit them before going live. You can also reject them with feedback
                        </p>
                    </div>
                )}
                {activeSubTab === "postings" && activeLens !== "governance" && (
                    <div className="mb-6 animate-in fade-in duration-200">
                        <div className="text-[10px] font-black uppercase tracking-widest text-[#007a8a]">Opportunity Registry</div>
                        <h3 className="text-xl font-bold text-[#003d45] mt-1 mb-2">Postings</h3>
                        <p className="text-xs text-gray-500 max-w-2xl leading-relaxed">
                            All active volunteer postings currently open, filled, or completed on the platform.
                        </p>
                    </div>
                )}
                {activeSubTab === "queue" && activeLens !== "governance" && (
                    <div className="mb-6 animate-in fade-in duration-200">
                        <div className="text-[10px] font-black uppercase tracking-widest text-[#007a8a]">Candidate Pipeline</div>
                        <h3 className="text-xl font-bold text-[#003d45] mt-1 mb-2">Interest Queue</h3>
                        <p className="text-xs text-gray-500 max-w-2xl leading-relaxed">
                            Review members who raised their hands to volunteer. Access their dossiers and assign them to roles.
                        </p>
                    </div>
                )}
                {activeSubTab === "assignments" && activeLens !== "governance" && (
                    <div className="mb-6 animate-in fade-in duration-200">
                        <div className="text-[10px] font-black uppercase tracking-widest text-[#007a8a]">Active Contributors</div>
                        <h3 className="text-xl font-bold text-[#003d45] mt-1 mb-2">Active Assignments</h3>
                        <p className="text-xs text-gray-500 max-w-2xl leading-relaxed">
                            Current active volunteers engaged in active project tasks and committee assignments.
                        </p>
                    </div>
                )}
                {activeSubTab === "pools" && activeLens !== "governance" && (
                    <div className="mb-6 animate-in fade-in duration-200">
                        <div className="text-[10px] font-black uppercase tracking-widest text-[#007a8a]">Availability Pools</div>
                        <h3 className="text-xl font-bold text-[#003d45] mt-1 mb-2">Standing Availability Pools</h3>
                        <p className="text-xs text-gray-500 max-w-2xl leading-relaxed">
                            Volunteers who have raised their hand to join standing availability pools. Expand a pool to view registered members.
                        </p>
                    </div>
                )}
                {activeSubTab === "ethics_intake" && activeLens === "ethics" && (
                    <div className="mb-6 animate-in fade-in duration-200">
                        <div className="text-[10px] font-black uppercase tracking-widest text-rose-600">Confidential Reports</div>
                        <h3 className="text-xl font-bold text-[#003d45] mt-1 mb-2">Ethics Intake Queue</h3>
                        <p className="text-xs text-gray-500 max-w-2xl leading-relaxed">
                            Retrieve and review confidential ethics violation reports. Access is strictly restricted to authorized staff.
                        </p>
                    </div>
                )}

                {dataError && (
                    <div className="bg-rose-50 text-rose-800 p-4 border border-rose-100 rounded-lg text-xs flex items-center gap-2 font-semibold">
                        <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                        <span><strong>Error:</strong> {dataError}</span>
                    </div>
                )}

                <div className="min-h-[40vh]">
                    {activeSubTab === "governance_overview" && activeLens === "governance" ? (
                        <GovernanceContributionTable onSelectVolunteer={(id) => setSelectedVolunteerId(id)} />
                    ) : activeSubTab === "gig_types" && activeLens === "governance" ? (
                        <ConsoleGigTypesPanel />

                    ) : activeSubTab === "ethics_intake" && activeLens === "ethics" ? (
                        <EthicsIntakeTable intakes={ethicsIntakes} isLoading={isLoadingData} />
                    ) : activeSubTab === "review_queue" ? (
                        <ConsoleApprovalQueue 
                            postings={pendingPostings} 
                            isLoading={isLoadingData} 
                            onActionSuccess={fetchLensData} 
                        />
                    ) : activeSubTab === "postings" ? (
                        <ConsolePostingTable 
                            postings={postings} 
                            isLoading={isLoadingData} 
                            onActionSuccess={fetchLensData}
                        />
                    ) : activeSubTab === "queue" ? (
                        <ConsoleInterestQueue 
                            applications={applications} 
                            isLoading={isLoadingData} 
                            onSelectVolunteer={(id) => setSelectedVolunteerId(id)} 
                            onActionSuccess={fetchLensData}
                        />
                    ) : activeSubTab === "assignments" ? (
                        <ConsoleAssignmentTable 
                            assignments={assignments} 
                            isLoading={isLoadingData} 
                            onSelectVolunteer={(id) => setSelectedVolunteerId(id)} 
                            onActionSuccess={fetchLensData}
                        />
                    ) : activeSubTab === "pools" ? (
                        <ConsoleStandingPoolsTable 
                            lens={activeLens}
                            onSelectVolunteer={(id) => setSelectedVolunteerId(id)}
                        />
                    ) : null}
                </div>
            </div>

            {/* Volunteer Details View (Modal Overlay) */}
            {selectedVolunteerId && activeLens && (
                <ConsoleVolunteerView 
                    volunteerId={selectedVolunteerId}
                    lens={activeLens}
                    onClose={() => setSelectedVolunteerId(null)}
                />
            )}
        </div>
    );
}

// ── SECURE CONFIDENTIAL INTAKE TABLE (ETHICS LENS) ────────────────────

function EthicsIntakeTable({ intakes, isLoading }: { intakes: any[]; isLoading: boolean; }) {
    const [viewingPayload, setViewingPayload] = useState<any | null>(null);

    const TEAL = "#007a8a";
    const DARK = "#003d45";

    if (isLoading) {
        return (
            <div className="py-12 flex flex-col items-center justify-center gap-3">
                <div className="w-8 h-8 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Querying secure vaults...</p>
            </div>
        );
    }

    if (intakes.length === 0) {
        return (
            <div className="bg-white border border-gray-150 rounded-xl p-12 shadow-sm text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4 border border-emerald-100">
                    <Shield className="w-6 h-6" />
                </div>
                <h4 className="text-sm font-bold text-[#003d45] uppercase tracking-wider">Queue Cleared</h4>
                <p className="text-xs text-gray-400 mt-2 max-w-sm mx-auto leading-relaxed">
                    All confidential ethics intake logs are currently empty. No active incident logs reported in this cycle.
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-150 rounded-xl shadow-sm overflow-hidden font-sans text-left">
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50 text-[10px] font-black uppercase tracking-widest text-gray-400 border-b border-gray-100">
                            <th className="py-4 px-6">Received At</th>
                            <th className="py-4 px-6">Gig Name</th>
                            <th className="py-4 px-6">Reporter</th>
                            <th className="py-4 px-6">Status</th>
                            <th className="py-4 px-6 text-right">Details</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 text-xs">
                        {intakes.map((intake) => (
                            <tr key={intake.id} className="hover:bg-gray-50/50 transition-colors">
                                <td className="py-4 px-6 font-semibold text-gray-500">
                                    {new Date(intake.received_at).toLocaleString()}
                                </td>
                                <td className="py-4 px-6 font-black text-[#003d45]">
                                    {intake.gig_type_name}
                                </td>
                                <td className="py-4 px-6 font-medium text-gray-600">
                                    {intake.reporter_name}
                                </td>
                                <td className="py-4 px-6">
                                    <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest">
                                        {intake.status}
                                    </span>
                                </td>
                                <td className="py-4 px-6 text-right">
                                    <button
                                        onClick={() => setViewingPayload(intake)}
                                        className="bg-[#003d45] hover:bg-[#002b30] text-white px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-wider transition-colors"
                                    >
                                        Reveal Report
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Secure Detail Viewer Modal */}
            {viewingPayload && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
                    <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl border border-gray-150 overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-300">
                        {/* Modal Header */}
                        <div className="px-6 py-5 border-b border-gray-100 bg-[#ae232d]/5 flex justify-between items-center">
                            <div className="flex items-center gap-2 text-[#ae232d]">
                                <ShieldAlert className="w-5 h-5 shrink-0" />
                                <h3 className="text-xs font-black uppercase tracking-wider">Confidential Intake Vault</h3>
                            </div>
                            <button 
                                onClick={() => setViewingPayload(null)}
                                className="text-gray-400 hover:text-gray-600 p-1.5 hover:bg-gray-100 rounded-full transition-all"
                            >
                                <CloseIcon className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6 overflow-y-auto space-y-5 text-left">
                            <div className="grid grid-cols-2 gap-4 text-xs">
                                <div>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block">Gig Name</span>
                                    <span className="font-bold text-[#003d45]">{viewingPayload.gig_type_name}</span>
                                </div>
                                <div>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block">Reporter</span>
                                    <span className="font-bold text-[#003d45]">{viewingPayload.reporter_name}</span>
                                </div>
                                <div>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block">Received At</span>
                                    <span className="font-bold text-[#003d45]">{new Date(viewingPayload.received_at).toLocaleString()}</span>
                                </div>
                                <div>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block">Intake Hash ID</span>
                                    <span className="font-mono text-gray-400 text-[10px] block truncate">{viewingPayload.id}</span>
                                </div>
                            </div>

                            <div className="border-t border-gray-100 pt-5">
                                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-2">Detailed Report Content</span>
                                <div className="bg-red-50/20 border border-[#ae232d]/10 rounded-2xl p-4 font-mono text-xs text-slate-800 whitespace-pre-wrap leading-relaxed">
                                    {viewingPayload.payload?.note || "No report body text provided."}
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-2 shrink-0">
                            <button
                                onClick={() => setViewingPayload(null)}
                                className="bg-[#003d45] hover:bg-[#002b30] text-white px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest transition-colors"
                            >
                                Close Vault
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
